import React from 'react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import type { PontoMensal } from '../types';

interface Props {
  pontos: PontoMensal[];
  modoCompacto?: boolean;
}

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 });

const RotuloPercentual = (props: any) => {
  const { x, y, value } = props;
  return (
    <text x={x} y={y - 12} textAnchor="middle" fontSize={11} fontWeight={700} fill="#374151">
      {(value * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
    </text>
  );
};

const TooltipCustom = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-3 shadow-xl text-xs">
      <p className="font-bold text-gray-800 dark:text-white mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }} className="font-semibold">
          {p.name}: {p.dataKey === 'taxaInadimplencia' ? `${(p.value * 100).toFixed(1)}%` : fmtBRL(p.value)}
        </p>
      ))}
    </div>
  );
};

const EvolucaoInadimplenciaChart: React.FC<Props> = ({ pontos, modoCompacto = false }) => {
  if (modoCompacto) {
    const ultimo = pontos[pontos.length - 1] ?? null;
    return (
      <div>
        <h3 className="text-xs font-bold text-gray-800 dark:text-white mb-1 truncate">Evolução da Inadimplência</h3>
        <p className="text-sm font-bold text-red-600 dark:text-red-400 mb-1">
          {ultimo ? `${(ultimo.taxaInadimplencia * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : '—'}
          <span className="text-[10px] font-normal text-gray-400"> do emitido{ultimo ? ` em ${ultimo.mes}` : ''}</span>
        </p>
        <ResponsiveContainer width="100%" height={70}>
          <ComposedChart data={pontos} margin={{ left: 0, right: 0, top: 0, bottom: 0 }}>
            <Bar dataKey="vencido" fill="#dc2626" radius={[2, 2, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
      <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-1">
        Evolução da Inadimplência — valor vencido e % do emitido por mês
      </h3>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={pontos} margin={{ left: 0, right: 0, top: 24, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
          <XAxis dataKey="mes" tick={{ fontSize: 10 }} stroke="#9ca3af" />
          <YAxis yAxisId="valor" tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => fmtBRL(v)} width={70} />
          <YAxis yAxisId="taxa" orientation="right" tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} tick={{ fontSize: 11 }} stroke="#9ca3af" />
          <Tooltip content={<TooltipCustom />} />
          <Legend
            wrapperStyle={{ fontSize: 12 }}
            formatter={(value) =>
              value === 'Valor vencido (R$)' ? 'Valor vencido (R$)' : 'Taxa de inadimplência do mês (% vencido ÷ emitido)'
            }
          />
          <Bar yAxisId="valor" dataKey="vencido" name="Valor vencido (R$)" fill="#dc2626" radius={[4, 4, 0, 0]} />
          <Line yAxisId="taxa" type="monotone" dataKey="taxaInadimplencia" stroke="#ffffff" strokeWidth={7} dot={false} legendType="none" isAnimationActive={false} />
          <Line
            yAxisId="taxa"
            type="monotone"
            dataKey="taxaInadimplencia"
            name="Taxa de inadimplência do mês (% vencido ÷ emitido)"
            stroke="#5F6C37"
            strokeWidth={4}
            dot={{ r: 5, fill: '#5F6C37', stroke: '#ffffff', strokeWidth: 2 }}
            label={<RotuloPercentual />}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EvolucaoInadimplenciaChart;
